import { Citation } from "../shared/citation";
import { RevealTarget } from "../shared/editInfo";
import { DeckModel, DeckShapeModel, parseSegmentId } from "./deckModel";
import { locateShape, locateSlide } from "./locate";

// PowerPoint's citation vocabulary: slide IDs (s3) and shape IDs (s3/sh17). The
// `<segment=ID>` tag convention is shared with every host; this is the half that knows
// what an ID means, what to label it, and how to get there. The IDs themselves, and the
// regex the chat matches them with, are defined alongside the deck model.

const truncate = (text: string, limit: number) =>
  text.length > limit ? `${text.substring(0, limit - 3)}...` : text;

/** One line of a shape's text, whitespace flattened, for a label. */
const summarise = (shape: DeckShapeModel): string => {
  const text = (shape.text ?? "").replace(/\s+/g, " ").trim();
  if (text.length > 0) return text;
  // No text: the shape's own name is the next best thing a reader can recognise, and it
  // is usually descriptive ("Picture 4", "Chart 2").
  return shape.name || shape.type;
};

/**
 * The shape most likely to be a slide's title, for labelling a slide-level citation.
 *
 * PowerPoint does say which placeholder is the title -- `Shape.placeholderFormat.type` --
 * but only from requirement set 1.8, above this add-in's 1.7 floor. `Shape.name` is
 * available from 1.3 and PowerPoint names the title placeholder "Title 1" or "Title
 * Placeholder 1" in every locale-neutral template, so a name match first and the first
 * text-bearing shape as a fallback gets the right answer nearly always and a reasonable
 * one otherwise. A label is a convenience; it is not worth a requirement-set gate.
 */
const titleOf = (shapes: DeckShapeModel[]): DeckShapeModel | undefined =>
  shapes.find((shape) => /title/i.test(shape.name) && (shape.text ?? "").trim().length > 0) ??
  shapes.find((shape) => (shape.text ?? "").trim().length > 0);

/**
 * Fills in the label shown in the chat's reference footer.
 *
 * Unlike Word's, this does no Office work at all and lays down no anchor. Word inserts a
 * bookmark here so a citation still navigates after later edits have shifted its
 * paragraph; PowerPoint has no bookmarks, and does not need them, because a shape ID is
 * already an identity rather than a position -- `reveal` can find the shape again
 * however the deck has been rearranged since. So this stays a pure lookup over the model
 * the answer was based on, which also makes it testable without a live PowerPoint.
 */
export const enrichCitations = async (citations: Citation[], deck: DeckModel): Promise<void> => {
  for (const citation of citations) {
    const parsed = parseSegmentId(citation.segmentId);
    if (!parsed) continue;

    const slide = deck.slides.find((candidate) => candidate.segmentId === `s${parsed.slideNumber}`);
    if (!slide) continue;

    if (parsed.shapeId) {
      const shape = slide.shapes.find((candidate) => candidate.shapeId === parsed.shapeId);
      if (shape) {
        citation.label = truncate(`Slide ${parsed.slideNumber}: ${summarise(shape)}`, 80);
      }
      continue;
    }

    const title = titleOf(slide.shapes);
    citation.label = title
      ? truncate(`Slide ${parsed.slideNumber}: ${summarise(title)}`, 80)
      : `Slide ${parsed.slideNumber}`;
  }
};

/**
 * Scrolls PowerPoint to a citation and selects it.
 *
 * Selecting the slide and selecting the shape are two separate syncs on purpose: shape
 * selection is scoped to a slide, and asking for it in the same batch that changes which
 * slide is current is asking the host to select a shape on a slide it has not switched to
 * yet.
 *
 * Both selection APIs (`Presentation.setSelectedSlides` and `Slide.setSelectedShapes`)
 * arrived in PowerPointApi 1.5, comfortably below the 1.7 the manifest already requires,
 * so no requirement-set gate is needed here. Note that shape selection hangs off `Slide`,
 * not off `Presentation` -- there is no `Presentation.setSelectedShapes`.
 */
export const reveal = async (target: RevealTarget): Promise<void> => {
  const parsed = parseSegmentId(target.segmentId);
  if (!parsed) return;

  try {
    await PowerPoint.run(async (context) => {
      if (parsed.shapeId) {
        // Located by shape id, so a citation still lands on the right shape even after
        // the deck has been reordered since the answer was written.
        const located = await locateShape(context, parsed.slideNumber, parsed.shapeId);
        context.presentation.setSelectedSlides([located.slide.id]);
        await context.sync();

        located.slide.setSelectedShapes([parsed.shapeId]);
        await context.sync();
        return;
      }

      const { slide } = await locateSlide(context, parsed.slideNumber);
      context.presentation.setSelectedSlides([slide.id]);
      await context.sync();
    });
  } catch {
    // A citation that cannot be reached -- the slide or shape has since been deleted -- is
    // a dead link in the footer, not a failure worth interrupting the user for. The chat
    // calls this fire-and-forget from a click handler and has nowhere to show an error.
  }
};
